import { useTranslation } from "react-i18next";
import { useEffect, useMemo, useState } from "react";
import {
  CreateHomeworkAssignmentRequestBody,
  GetTopicsCourseType,
} from "@packages/api/madlenAPI.schemas";
import CommonPropertiesStep from "./CommonPropertiesStep";
import { CourseData, isTurkishCourse } from "@packages/constant/CourseData";
import { firstTopicOfCourse } from "@packages/utils";
import { Button, Text } from "../../../../../core";
import CourseSelector from "../course-selector";
import GradeSelector from "../../../../tools/components/grade-selector";
import { useAppSelector } from "@packages/store/hooks";
import OutputLanguageSelector from "@features/tools/components/create-modal/OutputLanguageSelector";

export type CurriculumType = "MEB" | "INTERNATIONAL";
export type InternationalCurriculum = "AP" | "IGCSE" | "IB" | "ALEVEL";

const exampleTopics: Partial<Record<GetTopicsCourseType, string[]>> = {
  HISTORY: [
    "Osmanlı İmparatorluğu Duraklama Dönemi",
    "Türklerin İlk Yerleşim Alanları",
    "Kurtuluş Savaşı ve Cumhuriyet'in İlanı",
    "İstanbul'un Fethi ve Sonuçları",
    "Birinci Dünya Savaşı ve Nedenleri",
    "Sanayi Devrimi ve Etkileri",
    "Fransız Devrimi ve Avrupa'ya Etkileri",
    "Rönesans ve Reform Hareketleri",
    "Coğrafi Keşifler ve Sonuçları",
    "Türk Kurtuluş Savaşı'nın Aşamaları",
    "Atatürk İlkeleri ve İnkılapları",
    "Soğuk Savaş Dönemi",
  ],
  ENGLISH: [
    "Temel Zamanlar ve Kullanımları",
    "Okuma ve Anlama Teknikleri",
    "İngilizce Dilbilgisi Kuralları",
    "Sıfatlar ve Zarflar",
    "Edilgen Yapılar ve Kullanımları",
    "Koşul Cümleleri ve Türleri",
    "Dolaylı Anlatım",
    "Bağlaçlar ve Geçiş İfadeleri",
    "Fiil Kalıpları ve Deyimler",
    "Akademik Yazı Teknikleri",
    "İş İngilizcesi ve Resmi Yazışmalar",
    "Sunum Teknikleri ve İfade Becerileri",
  ],
  SCIENCE: [
    "Maddenin Halleri ve Değişimi",
    "Günlük Hayatta Basit Makineler",
    "Dünya, Güneş ve Ay İlişkisi",
    "Kuvvet ve Hareket İlişkisi",
    "Enerji Dönüşümleri ve Korunumu",
    "Işık ve Ses Dalgaları",
    "Elektrik Devreleri ve Manyetizma",
    "Canlıların Sınıflandırılması",
    "Ekosistemler ve Besin Zincirleri",
    "İnsan Vücudu ve Sistemleri",
    "Genetik ve Kalıtım",
    "Çevre Kirliliği ve Sürdürülebilirlik",
  ],
  GEOGRAPHY: [
    "İklim Tipleri ve Bitki Örtüsü",
    "Türkiye'nin Doğal Kaynakları",
    "Nüfus ve Yerleşme",
    "Türkiye'nin Jeopolitik Konumu",
    "Dünya'nın Şekli ve Hareketleri",
    "Harita Bilgisi ve Ölçekler",
    "Türkiye'nin Fiziki Coğrafyası",
    "Türkiye'nin Ekonomik Coğrafyası",
    "Doğal Afetler ve Korunma Yöntemleri",
    "Küresel İklim Değişikliği",
    "Göç ve Kentleşme",
    "Uluslararası Ulaşım Hatları",
  ],
  TURKISH: [
    "Paragrafta Ana Fikir ve Yardımcı Fikirler",
    "Sözcükte ve Cümlede Anlam",
    "Yazım Kuralları ve Noktalama İşaretleri",
    "Cümle Bilgisi ve Ögeleri",
    "Fiilimsiler ve Kullanımları",
    "Anlatım Bozuklukları",
    "Söz Sanatları",
    "Edebi Akımlar ve Özellikleri",
    "Divan Edebiyatı ve Özellikleri",
    "Cumhuriyet Dönemi Türk Edebiyatı",
    "Şiir Bilgisi ve Türleri",
    "Metin Türleri ve Özellikleri",
  ],
  MATHEMATICS: [
    "Rasyonel Sayılarla İşlemler",
    "Üçgenler ve Dörtgenler",
    "Oran ve Orantı",
    "Cebirsel İfadeler ve Özdeşlikler",
    "Denklemler ve Eşitsizlikler",
    "Fonksiyonlar ve Grafikleri",
    "Olasılık ve İstatistik",
    "Trigonometri ve Uygulamaları",
    "Logaritma ve Üstel Fonksiyonlar",
    "Türev ve İntegral",
    "Vektörler ve Analitik Geometri",
    "Permütasyon, Kombinasyon ve Binom",
  ],
  BIOLOGY: [
    "Hücre Bölünmesi ve Mitoz",
    "Hücre ve Organeller",
    "Canlıların Sınıflandırılması",
    "Sindirim Sistemi",
    "Dolaşım Sistemi",
    "Solunum Sistemi",
    "Boşaltım Sistemi",
    "Sinir Sistemi ve Duyu Organları",
    "Endokrin Sistem ve Hormonlar",
    "Üreme Sistemi ve Embriyonik Gelişim",
    "Genetik ve Kalıtım Mekanizmaları",
    "Evrim ve Doğal Seçilim",
  ],
  CHEMISTRY: [
    "Maddenin Tanecikli Yapısı",
    "Karışımlar ve Özellikleri",
    "Asitler ve Bazlar",
    "Periyodik Tablo ve Özellikleri",
    "Kimyasal Bağlar ve Molekül Geometrisi",
    "Kimyasal Tepkimeler ve Denklemler",
    "Gazlar ve Özellikleri",
    "Çözeltiler ve Derişim Birimleri",
    "Kimyasal Denge ve Le Chatelier İlkesi",
    "Termokimya ve Entalpi",
    "Elektrokimya ve Piller",
    "Organik Kimya ve Fonksiyonel Gruplar",
  ],
  PHYSICS: [
    "Kuvvet ve Hareket",
    "Işık ve Ses",
    "Elektrik Devreleri",
    "Newton'un Hareket Yasaları",
    "İş, Güç ve Enerji",
    "Momentum ve Çarpışmalar",
    "Dalgalar ve Dalga Özellikleri",
    "Elektrik Alan ve Potansiyel",
    "Manyetizma ve Elektromanyetik İndüksiyon",
    "Atom Modelleri ve Kuantum Fiziği",
    "Termodinamik Yasaları",
    "Görelilik Teorisi",
  ],
  RELIGION: [
    "İbadetler ve Önemi",
    "Ahlak ve Değerler",
    "Peygamberlerin Hayatı",
    "İslam'ın Temel İnanç Esasları",
    "Kur'an-ı Kerim ve Tefsir",
    "Hadis ve Sünnet",
    "İslam Tarihi ve Medeniyeti",
    "Dinler Tarihi ve Karşılaştırmalı Dinler",
    "İslam Hukuku ve Temel İlkeleri",
    "Tasavvuf ve Tarikatlar",
    "Güncel Dini Meseleler",
    "İslam Düşünce Tarihi",
  ],
  PHILOSOPHY: [
    "Felsefenin Temel Konuları",
    "Bilgi Felsefesi",
    "Ahlak Felsefesi",
    "Varlık Felsefesi (Ontoloji)",
    "Bilim Felsefesi",
    "Siyaset Felsefesi",
    "Sanat Felsefesi (Estetik)",
    "Din Felsefesi",
    "Antik Yunan Felsefesi",
    "Orta Çağ Felsefesi",
    "Modern Felsefe",
    "Çağdaş Felsefe Akımları",
  ],
  DIGITAL_CITIZENSHIP: [
    "İnternet Güvenliği",
    "Dijital Ayak İzi",
    "Siber Zorbalık",
    "Sosyal Medya Okuryazarlığı",
    "Dijital Kimlik ve Mahremiyet",
    "Çevrimiçi Etik ve Davranış Kuralları",
    "Telif Hakları ve Dijital İçerik",
    "Dijital Araçlarla İşbirliği",
    "Eleştirel Dijital Okuryazarlık",
    "Teknoloji Bağımlılığı",
    "Dijital Demokrasi ve Katılım",
    "Yapay Zeka ve Etik",
  ],
  CITIZENSHIP: [
    "Vatandaşlık Hakları ve Sorumlulukları",
    "Demokrasi ve Katılım",
    "Toplumsal Değerler",
    "İnsan Hakları ve Özgürlükler",
    "Hukuk Devleti ve Adalet",
    "Sivil Toplum ve Örgütlenme",
    "Yerel ve Küresel Vatandaşlık",
    "Sürdürülebilir Kalkınma",
    "Kültürel Çeşitlilik ve Hoşgörü",
    "Medya Okuryazarlığı",
    "Ekonomik Okuryazarlık",
    "Aktif Vatandaşlık Projeleri",
  ],
  AP_ASTRONOMY: [
    "Solar System and Planetary Motion",
    "Star Formation and Life Cycles",
    "Galaxies and the Universe",
    "Celestial Mechanics and Kepler's Laws",
    "Stellar Evolution and Nucleosynthesis",
    "Cosmology and the Big Bang Theory",
    "Black Holes and Neutron Stars",
    "Exoplanets and Detection Methods",
    "Telescopes and Observational Techniques",
    "Astrobiology and the Search for Life",
    "Space Exploration and Technology",
    "Dark Matter and Dark Energy",
  ],
  AP_PSYCHOLOGY: [
    "Cognitive Development Stages",
    "Behavioral Conditioning",
    "Psychological Disorders",
    "Neurobiology and Neural Transmission",
    "Memory Formation and Retrieval",
    "Personality Theories and Assessment",
    "Social Psychology and Group Dynamics",
    "Motivation and Emotion",
    "Sensation and Perception",
    "Learning and Information Processing",
    "Psychological Research Methods",
    "Therapeutic Approaches and Treatment",
  ],
  AP_BUSINESS: [
    "Marketing Strategies and Analysis",
    "Financial Management Basics",
    "Organizational Behavior",
    "Business Ethics and Corporate Responsibility",
    "Entrepreneurship and Innovation",
    "Supply Chain Management",
    "Human Resource Management",
    "Strategic Planning and Implementation",
    "Business Law and Regulations",
    "International Business and Globalization",
    "Leadership and Decision Making",
    "Digital Business and E-commerce",
  ],
  AP_ECONOMICS: [
    "Market Equilibrium and Price Theory",
    "Fiscal and Monetary Policy",
    "International Trade",
    "Supply and Demand Analysis",
    "Economic Growth and Development",
    "Inflation and Unemployment",
    "Banking Systems and Financial Markets",
    "Market Structures and Competition",
    "Labor Markets and Wage Determination",
    "Government Intervention and Regulation",
    "Macroeconomic Indicators and Measurement",
    "Game Theory and Strategic Behavior",
  ],
  AP_SOCIOLOGY: [
    "Social Stratification",
    "Cultural Norms and Values",
    "Social Institutions and Change",
    "Socialization Process and Agents",
    "Deviance and Social Control",
    "Race and Ethnicity",
    "Gender and Sexuality",
    "Family Structures and Dynamics",
    "Religion and Society",
    "Education and Social Mobility",
    "Urbanization and Population Dynamics",
    "Research Methods in Sociology",
  ],
  AP_WORLD_HISTORY_1: [
    "Ancient River Valley Civilizations",
    "Classical Empires and Trade",
    "Post-Classical Era",
    "Early Agricultural Societies",
    "Development of World Religions",
    "Byzantine Empire and Eastern Christianity",
    "Islamic Caliphates and Cultural Achievements",
    "Medieval Europe and Feudalism",
    "Mongol Conquests and Eurasian Integration",
    "African Kingdoms and Trade Networks",
    "Pre-Columbian American Civilizations",
    "East Asian Dynasties and Cultural Exchange",
  ],
  AP_WORLD_HISTORY_2: [
    "Age of Revolutions",
    "Imperialism and Colonization",
    "20th Century Conflicts",
    "Renaissance and Scientific Revolution",
    "Atlantic Slave Trade and Its Impact",
    "Industrial Revolution and Global Effects",
    "Nationalism and Nation-State Formation",
    "World War I and Its Aftermath",
    "Great Depression and Global Responses",
    "World War II and the Holocaust",
    "Cold War and Decolonization",
    "Globalization and Contemporary Issues",
  ],
  AP_BIOLOGY: [
    "Cell Division and Reproduction",
    "DNA Structure and Replication",
    "Evolution and Natural Selection",
    "Cellular Respiration and Photosynthesis",
    "Mendelian Genetics and Inheritance Patterns",
    "Gene Expression and Regulation",
    "Biotechnology and Genetic Engineering",
    "Ecology and Ecosystem Dynamics",
    "Plant Structure and Function",
    "Animal Physiology and Homeostasis",
    "Immune System and Disease Defense",
    "Biodiversity and Conservation",
  ],
  AP_CHEMISTRY: [
    "Thermodynamics and Energy",
    "Chemical Equilibrium",
    "Organic Chemistry Basics",
    "Atomic Structure and Electron Configuration",
    "Chemical Bonding and Molecular Geometry",
    "Kinetics and Reaction Rates",
    "Acids, Bases, and pH",
    "Redox Reactions and Electrochemistry",
    "Solutions and Colligative Properties",
    "Nuclear Chemistry and Radioactivity",
    "States of Matter and Intermolecular Forces",
    "Laboratory Techniques and Safety",
  ],
  AP_PHYSICS: [
    "KinematICS and Dynamics",
    "Electric Fields and Forces",
    "Quantum Mechanics Introduction",
    "Circular Motion and Gravitation",
    "Work, Energy, and Power",
    "Linear Momentum and Collisions",
    "Rotational Motion and Angular Momentum",
    "Oscillations and Simple Harmonic Motion",
    "Electromagnetic Waves and Optics",
    "Fluid Mechanics and Thermodynamics",
    "Atomic and Nuclear Physics",
    "Special Relativity",
  ],
  AP_2025_BIOLOGY: [
    "Cellular Energetics",
    "Cell Communication and Cell Cycle",
    "Heredity and Gene Expression",
    "Natural Selection and Evolution",
    "Ecological Systems and Interactions",
    "Biological Macromolecules",
    "Membrane Transport and Cell Compartmentalization",
    "Signal Transduction Pathways",
    "Meiosis and Genetic Diversity",
    "Regulation of Gene Expression",
    "Biotechnology Applications",
    "Population Dynamics and Community Ecology",
  ],
  AP_2025_CALCULUS_AB_AND_BC: [
    "Limits and Continuity",
    "Differentiation: Definition and Fundamental Properties",
    "Integration and Accumulation of Change",
    "Differential Equations and Mathematical Modeling",
    "Applications of Differentiation",
    "Applications of Integration",
    "Parametric Equations and Polar Coordinates",
    "Infinite Sequences and Series",
    "Taylor Series and Approximations",
    "Vector-Valued Functions",
    "Partial Derivatives and Multiple Integration",
    "L'Hôpital's Rule and Indeterminate Forms",
  ],
  AP_2025_CHEMISTRY: [
    "Atomic Structure and Properties",
    "Molecular and Ionic Compound Structure",
    "Chemical Reactions and Stoichiometry",
    "Kinetics and Reaction Mechanisms",
    "Thermodynamics and Spontaneity",
    "Equilibrium and Le Chatelier's Principle",
    "Acids and Bases: Theories and Reactions",
    "Redox Processes and Electrochemistry",
    "Spectroscopy and Structural Determination",
    "Organic Chemistry: Functional Groups",
    "Intermolecular Forces and Properties",
    "Chemical Bonding and Molecular Geometry",
  ],
  AP_2025_COMPUTER_SCIENCE_A: [
    "Object-Oriented Program Design",
    "Program Implementation",
    "Program Analysis",
    "Data Structures and Algorithms",
    "Computing in Context",
    "Classes and Object-Oriented Design",
    "Arrays and ArrayLists",
    "Inheritance and Polymorphism",
    "Recursion and Recursive Algorithms",
    "Sorting and Searching Algorithms",
    "Exception Handling and Program Correctness",
    "Ethical Computing and Security",
  ],
  AP_2025_COMPUTER_SCIENCE_PRINCIPLES: [
    "Creative Development",
    "Data Analysis",
    "Algorithms and Programming",
    "Computer Systems and Networks",
    "Impact of Computing",
    "Computational Thinking Practices",
    "Internet Architecture and Protocols",
    "Data Representation and Abstraction",
    "Program Design and Development",
    "Cybersecurity and Privacy",
    "Artificial Intelligence and Machine Learning",
    "Collaborative Computing",
  ],
  AP_2025_ENVIRONMENTAL_SCIENCE: [
    "Ecosystems and Energy Flow",
    "Biodiversity and Population Dynamics",
    "Pollution and Climate Change",
    "Earth Systems and Resources",
    "Land and Water Use",
    "Energy Resources and Consumption",
    "Atmospheric Pollution and Climate Change",
    "Aquatic and Terrestrial Pollution",
    "Global Change and Sustainability",
    "Environmental Policy and Governance",
    "Conservation Biology and Restoration",
    "Human Population Growth and Resource Consumption",
  ],
  AP_2025_PHYSICS_1: [
    "Kinematics",
    "Dynamics",
    "Circular Motion and Gravitation",
    "Energy and Conservation of Energy",
    "Momentum and Collisions",
    "Simple Harmonic Motion",
    "Torque and Rotational Motion",
    "Electric Charge and Electric Force",
    "DC Circuits and Resistors",
    "Mechanical Waves and Sound",
    "Fluid Mechanics and Pressure",
    "Thermal Physics and Heat Transfer",
  ],
  AP_2025_PHYSICS_2: [
    "Thermodynamics",
    "Electric Force, Field, and Potential",
    "Electric Circuits",
    "Magnetism and Electromagnetic Induction",
    "Geometric and Physical Optics",
    "Quantum Physics and Atomic Models",
    "Nuclear Physics and Radioactivity",
    "Fluids: Pressure and Forces",
    "Heat Engines and Entropy",
    "Capacitance and Dielectrics",
    "Electromagnetic Waves",
    "Quantum Effects and Uncertainty",
  ],
  AP_2025_PHYSICS_C_ELECTRICITY_AND_MAGNETISM: [
    "Electrostatics",
    "Conductors, Capacitors, and Dielectrics",
    "Electric Circuits",
    "Magnetic Fields",
    "Electromagnetism",
    "Electric Field and Gauss's Law",
    "Electric Potential and Energy",
    "Capacitance and Capacitors",
    "Current, Resistance, and DC Circuits",
    "Magnetic Forces and Fields",
    "Electromagnetic Induction",
    "Maxwell's Equations and EM Waves",
  ],
  AP_2025_PHYSICS_C_MECHANICS: [
    "Kinematics",
    "Newton's Laws of Motion",
    "Work, Energy, and Power",
    "Systems of Particles and Linear Momentum",
    "Circular Motion and Rotation",
    "Oscillations",
    "Gravitation",
    "Vector Calculus in Mechanics",
    "Rotational Kinematics and Dynamics",
    "Angular Momentum and Its Conservation",
    "Equilibrium and Elasticity",
    "Orbital Motion and Kepler's Laws",
  ],
  AP_2025_PRECALCULUS: [
    "Functions and Their Properties",
    "Polynomial and Rational Functions",
    "Trigonometric and Polar Functions",
    "Exponential and Logarithmic Functions",
    "Vector and Matrix Operations",
    "Complex Numbers and Operations",
    "Conic Sections and Parametric Equations",
    "Sequences, Series, and Mathematical Induction",
    "Limits and Continuity Introduction",
    "Probability and Combinatorics",
    "Transformations of Functions",
    "Applications of Functions in Modeling",
  ],
  AP_2025_STATISTICS: [
    "Data Analysis and Representation",
    "Probability and Random Variables",
    "Statistical Inference",
    "Sampling and Experimental Design",
    "Descriptive Statistics and Graphical Displays",
    "Probability Distributions",
    "Sampling Distributions and Estimation",
    "Hypothesis Testing",
    "Correlation and Regression",
    "Chi-Square Tests and Inference",
    "Confidence Intervals",
    "Statistical Research Methods",
  ],
  IGCSE_BIOLOGY: [
    "Characteristics and classification of living organisms",
    "Organisation of the organism",
    "Movement into and out of cells",
    "Biological molecules",
    "Enzymes",
    "Plant nutrition",
    "Human nutrition",
    "Transport in plants",
    "Transport in animals",
    "Diseases and immunity",
    "Gas exchange in humans",
    "Respiration",
  ],
  IGCSE_PHYSICS: [
    "Motion, forces and energy",
    "Thermal physics",
    "Waves",
    "Electricity and magnetism",
    "Nuclear physics",
    "Space physics",
  ],
  IGCSE_CHEMISTRY: [
    "States of matter",
    "Atoms, elements and compounds",
    "Stoichiometry",
    "Electrochemistry",
    "Chemical energetics",
    "Chemical reactions",
    "Acids, bases and salts",
    "The Periodic Table",
    "Metals",
    "Chemistry of the environment",
    "Organic chemistry",
    "Experimental techniques and chemical analysis",
  ],
  IGCSE_BUSINESS_STUDIES: [
    "Understanding business activity",
    "People in business",
    "Marketing",
    "Operations management",
    "Financial information and decisions",
    "External influences on business activity",
  ],
  IGCSE_ECONOMICS: [
    "The basic economic problem",
    "The allocation of resources",
    "Microeconomic decision makers",
    "Government and the macroeconomy",
    "Economic development",
    "International trade and globalisation",
  ],
  IGCSE_COMPUTER_SCIENCE: [
    "Data representation",
    "Data transmission",
    "Hardware",
    "Software",
    "The internet and its uses",
    "Automated and emerging technologies",
    "Algorithm design and problem-solving",
    "Programming",
    "Databases",
    "Boolean logic",
  ],
  IGCSE_GEOGRAPHY: [
    "Population and settlement",
    "The natural environment",
    "Economic development",
  ],
  IGCSE_HISTORY: [
    "Core content: Option A: The nineteenth century: the development of modern nation states, 1848–1914",
    "Core content: Option B: The twentieth century: international relations since 1919",
    "Depth study A: The First World War, 1914–18",
    "Depth study B: Germany, 1918–45",
    "Depth study C: Russia, 1905–41",
    "Depth study D: The United States, 1919–41",
    "Depth study E: The Second World War in Europe and the Asia–Pacific, 1939–c.1945",
  ],
  IGCSE_MATHEMATICS: [
    "Number",
    "Algebra and graphs",
    "Coordinate geometry",
    "Geometry",
    "Mensuration",
    "Trigonometry",
    "Transformations and vectors",
    "Probability",
    "Statistics",
  ],
  ALEVEL_BUSSINESS: [
    "Business and its environment (AS Level)",
    "Human resource management (AS Level)",
    "Marketing (AS Level)",
    "Operations management (AS Level)",
    "Finance and accounting (AS Level)",
    "Business and its environment (A Level)",
    "Human resource management (A Level)",
    "Marketing (A Level)",
    "Operations management (A Level)",
    "Finance and accounting (A Level)",
  ],
  ALEVEL_BIOLOGY: [
    "Cell structure (AS Level)",
    "Biological molecules (AS Level)",
    "Enzymes (AS Level)",
    "Cell membranes and transport (AS Level)",
    "The mitotic cell cycle (AS Level)",
    "Nucleic acids and protein synthesis (AS Level)",
    "Transport in plants (AS Level)",
    "Transport in mammals (AS Level)",
    "Gas exchange (AS Level)",
    "Infectious diseases (AS Level)",
  ],
  ALEVEL_CHEMISTRY: [
    "Atomic structure",
    "Atoms, molecules and stoichiometry",
    "Chemical bonding",
    "States of matter",
    "Chemical energetics",
    "Electrochemistry",
    "Equilibria",
    "Reaction kinetics",
    "The Periodic Table: chemical periodicity",
    "Group 2",
  ],
  ALEVEL_COMPUTER_SCIENCE: [
    "Information representation",
    "Communication",
    "Hardware",
    "Processor Fundamentals",
    "System Software",
    "Security, privacy and data integrity",
    "Ethics and Ownership",
    "Databases",
    "Algorithm Design and Problem-solving",
    "Data Types and Structures",
  ],
  ALEVEL_ECONOMICS: [
    "Basic economic ideas and resource allocation (AS Level)",
    "The price system and the microeconomy (AS Level)",
    "Government microeconomy intervention (AS Level)",
    "The Macroeconomy (AS Level)",
    "Government macroeconomic intervention (AS Level)",
    "International economic issues (AS Level)",
    "The price system and the microeconomy (A Level)",
    "Government microeconomic intervention (A Level)",
    "The macroeconomy (A Level)",
    "Government macroeconomic intervention (A Level)",
  ],
  ALEVEL_EUROPEAN_HISTORY: [
    "Modern Europe, 1774-1924: France, 1774–1814",
    "Modern Europe, 1750–1924: Liberalism and nationalism in Germany, 1815–71",
    "Modern Europe, 1750–1924: Russia from autocracy to revolution, 1881–1924",
    "The origins of the First World War",
    "European history, 1919–41: Mussolini's Italy, 1919-41",
    "European history, 1919–41: Stalin's Russia, 1924-41",
    "European history, 1919–41: Hitler's Germany, 1929-41",
  ],
  ALEVEL_FURTHER_MATHEMATICS: [
    "Further Pure Mathematics 1 (for Paper 1)",
    "Further Pure Mathematics 2 (for Paper 2)",
    "Further Mechanics (for Paper 3)",
    "Further Probability & Statistics (for Paper 4)",
  ],
  ALEVEL_GEOGRAPHY: [
    "Hydrology and fluvial geomorphology",
    "Atmosphere and weather",
    "Rocks and weathering",
    "Population",
    "Migration",
    "Settlement dynamics",
    "Tropical environments",
    "Coastal environments",
    "Hazardous environments",
    "Hot arid and semi-arid environments",
  ],
  ALEVEL_INTERNATIONAL_HISTORY: [
    "Imperialism and the emergence of world powers c.1870–1918",
    "International relations 1919–29: conflict and cooperation",
    "International history 1929–39: the rise of extremism and the road to war",
    "The Soviet Empire in Eastern Europe, 1953–91",
    "End of minority rule in South Africa and Zimbabwe, 1948–94",
    "The route to independence: Malaysia and Indonesia c.1909-67",
  ],
  ALEVEL_MATHEMATICS: [
    "Pure Mathematics 1 (for Paper 1)",
    "Pure Mathematics 2 (for Paper 2)",
    "Pure Mathematics 3 (for Paper 3)",
    "Mechanics (for Paper 4)",
    "Probability & Statistics 1 (for Paper 5)",
    "Probability & Statistics 2 (for Paper 6)",
  ],
  ALEVEL_PHYSICS: [
    "Physical quantities and units",
    "Kinematics",
    "Dynamics",
    "Forces, density and pressure",
    "Work, energy and power",
    "Deformation of solids",
    "Waves",
    "Superposition",
    "Electricity",
    "D.C. circuits",
  ],
  ALEVEL_SOCIOLOGY: [
    "Socialisation and the creation of social identity",
    "Methods of research",
    "Theories of the family and social change",
    "Family roles and changing relationships",
    "Education and society",
    "Education and inequality",
    "Key debates, concepts and perspectives",
    "Contemporary issues",
    "Ownership and control of the media",
    "Media representation and effects",
  ],
  ALEVEL_US_HISTORY_SINCE_1877: [
    "The Gilded Age and Progressive Era",
    "American Imperialism, the First World War, and the 1920s",
    "The Great Depression, the Second World War, and the Early Cold War",
    "The Development of the US in the 1960s and 1970s",
  ],
  ALEVEL_US_HISTORY_TO_1877: [
    "The American Civil War and Reconstruction, 1846–77.",
    "Indigenous Societies, European Colonization, and the Atlantic World, c.1400s–c.1800",
    "The American Revolution and the Emergence of a New Nation, c.1765–c.1815",
    "A Changing New Nation, c.1820–c.1850",
    "The Impacts of Expansion, c.1800–77.",
  ],
  UNKNOWN: [
    "General Knowledge Topics",
    "Interdisciplinary Concepts",
    "Critical Thinking Exercises",
    "Problem-Solving Strategies",
    "Research Methods and Techniques",
    "Communication Skills Development",
    "Creative Thinking and Innovation",
    "Ethical Decision Making",
    "Global Perspectives and Issues",
    "Technology and Society",
    "Personal Development and Well-being",
    "Collaborative Learning Projects",
  ],
};

const exampleQuestionStyles = {
  tr: [
    {
      title: "Yeni Nesil Yorumlama",
      description:
        "Bilginin karmaşık, senaryo tabanlı bağlamlarda uygulanmasına odaklanan sorular. Ezberden ziyade verilerin ve kavramların yorumlanmasını ön planda tutar ve genellikle birden fazla adım ya da senaryo içeren uzun yönlendirmeler kullanır.",
    },
    {
      title: "Analitik Düşünme",
      description:
        "Öğrencilerin bir fikir, olay veya sürecin farklı bileşenlerini ayırıp incelemelerini ve altta yatan yapıyı anlamalarını gerektiren sorular. Elemanlar arasındaki bağlantıları kurmaya ve ilişkileri analiz etmeye odaklanır.",
    },
    {
      title: "Eleştirel Düşünme",
      description:
        "Öğrencilerin farklı bakış açılarını değerlendirmesini, argümanları ölçmesini ve bilgilerin geçerliliğini belirlemesini sağlayan sorular. Varsayımları sorgulamayı ve mantıklı sonuçlara ulaşmayı teşvik eder.",
    },
    {
      title: "Kavramsal Anlayış",
      description:
        "Öğrencinin temel kavramları kavrama düzeyini değerlendiren sorular. Ezberlenmiş detaylar yerine, öğrencilerin ana fikirleri kendi kelimeleriyle açıklamalarını gerektirir.",
    },
    {
      title: "Problem Çözme",
      description:
        "Öğrencilere pratik bir problem sunan ve çözüm geliştirmelerini gerektiren sorular. Öğrencilerin bilgilerini kullanarak gerçek zorlukların üstesinden gelme becerilerini değerlendirmeye yardımcı olur.",
    },
    {
      title: "Gerçek Dünya Uygulaması",
      description:
        "Öğrencilerin teorik kavramları gerçek yaşam durumlarına uygulamalarını gerektiren sorular. Pratik becerileri test etmek ve bilginin sınıf dışında nasıl faydalı olabileceğini göstermek için tasarlanmıştır.",
    },
    {
      title: "Karşılaştırmalı Analiz",
      description:
        "Öğrencilerin birden fazla fikir, teori veya olayı karşılaştırıp farklılıklarını ve benzerliklerini vurgulamalarını isteyen sorular. Öğrencileri benzerlikleri ve farklılıkları eleştirel bir şekilde değerlendirmeye ve nüanslı bakış açılarını ifade etmeye teşvik eder.",
    },
    {
      title: "Sentez",
      description:
        "Öğrencilerin birden fazla bilgiyi bir araya getirerek bütüncül bir anlayış oluşturmalarını veya yeni bir fikir yaratmalarını gerektiren sorular. Yaratıcı düşünmeyi ve yeniliği teşvik eder.",
    },
    {
      title: "Yorumlama ve Uygulama",
      description:
        "Verilen veriyi, metni veya durumu yorumlayıp bunu bir problemi çözmek için uygulamaya odaklanan sorular. Öğrencinin çıkarım yapma ve bilgiyi pratikte kullanma becerisini değerlendirir.",
    },
    {
      title: "Kısa Cevaplı",
      description:
        "Kısa fakat tam cevaplar gerektiren sorular. Öğrencilerin doğrudan ve net cevaplar vererek konuyu etkin bir şekilde anladıklarını göstermelerine odaklanır.",
    },
  ],
  en: [
    {
      title: "Next Generation Interpretation",
      description:
        "Questions that focus on applying knowledge in complex, scenario-based contexts. They emphasize the interpretation of data and concepts rather than rote recall, often using longer prompts that involve multiple steps or scenarios.",
    },
    {
      title: "Analytical Thinking",
      description:
        "Questions that require students to dissect and examine different components of an idea, event, or process to understand the underlying structure. They focus on drawing connections between elements and analyzing relationships.",
    },
    {
      title: "Conceptual Understanding",
      description:
        "Questions that evaluate the student's grasp of fundamental concepts. Instead of focusing on memorized details, these questions require students to explain core ideas in their own words.",
    },
    {
      title: "Problem Solving",
      description:
        "Questions that present a practical problem and require students to develop a solution. These questions help in assessing the students' ability to apply their knowledge to overcome real challenges.",
    },
    {
      title: "Real-world Application",
      description:
        "Questions that require students to apply theoretical concepts to real-life situations. They are designed to test practical skills and demonstrate how knowledge can be useful beyond the classroom.",
    },
    {
      title: "Comparative Analysis",
      description:
        "Questions that ask students to compare and contrast multiple ideas, theories, or events. They encourage students to critically assess similarities and differences and articulate nuanced perspectives.",
    },
    {
      title: "Synthesis",
      description:
        "Questions that require students to combine multiple pieces of information to form a cohesive understanding or create a new idea. These questions encourage creative thinking and innovation.",
    },
    {
      title: "Interpretation and Application",
      description:
        "Questions that focus on interpreting given data, text, or a situation and applying it to solve a problem. They assess the student's ability to draw inferences and apply knowledge practically.",
    },
    {
      title: "Short Answer",
      description:
        "Questions that require concise but complete answers. These questions focus on students' ability to provide direct, to-the-point responses that demonstrate their understanding effectively.",
    },
  ],
  // Added German question styles
  de: [
    {
      title: "Neue Generation Interpretation",
      description:
        "Fragen, die sich auf die Anwendung von Wissen in komplexen, szenariobasierten Kontexten konzentrieren. Sie betonen die Interpretation von Daten und Konzepten anstatt des Auswendiglernens und verwenden oft längere Aufgabenstellungen mit mehreren Schritten oder Szenarien.",
    },
    {
      title: "Analytisches Denken",
      description:
        "Fragen, die von Schülern verlangen, verschiedene Komponenten einer Idee, eines Ereignisses oder Prozesses zu zerlegen und zu untersuchen, um die zugrundeliegende Struktur zu verstehen. Sie konzentrieren sich auf das Herstellen von Verbindungen zwischen Elementen und die Analyse von Beziehungen.",
    },
    {
      title: "Kritisches Denken",
      description:
        "Fragen, die Schüler dazu bringen, verschiedene Perspektiven zu bewerten, Argumente zu messen und die Gültigkeit von Informationen zu bestimmen. Sie fördern das Hinterfragen von Annahmen und das Erreichen logischer Schlussfolgerungen.",
    },
    {
      title: "Konzeptionelles Verständnis",
      description:
        "Fragen, die das Verständnis grundlegender Konzepte des Schülers bewerten. Anstatt sich auf auswendig gelernte Details zu konzentrieren, verlangen diese Fragen von Schülern, Kernideen in ihren eigenen Worten zu erklären.",
    },
    {
      title: "Problemlösung",
      description:
        "Fragen, die ein praktisches Problem präsentieren und von Schülern eine Lösung verlangen. Diese Fragen helfen bei der Bewertung der Fähigkeit der Schüler, ihr Wissen anzuwenden, um echte Herausforderungen zu bewältigen.",
    },
    {
      title: "Reale Anwendung",
      description:
        "Fragen, die von Schülern verlangen, theoretische Konzepte auf reale Situationen anzuwenden. Sie sind darauf ausgelegt, praktische Fähigkeiten zu testen und zu zeigen, wie Wissen außerhalb des Klassenzimmers nützlich sein kann.",
    },
    {
      title: "Vergleichende Analyse",
      description:
        "Fragen, die Schüler auffordern, mehrere Ideen, Theorien oder Ereignisse zu vergleichen und zu kontrastieren. Sie ermutigen Schüler, Ähnlichkeiten und Unterschiede kritisch zu bewerten und nuancierte Perspektiven zu artikulieren.",
    },
    {
      title: "Synthese",
      description:
        "Fragen, die von Schülern verlangen, mehrere Informationsstücke zu kombinieren, um ein kohärentes Verständnis zu bilden oder eine neue Idee zu schaffen. Diese Fragen fördern kreatives Denken und Innovation.",
    },
    {
      title: "Interpretation und Anwendung",
      description:
        "Fragen, die sich auf die Interpretation gegebener Daten, Texte oder Situationen konzentrieren und diese zur Problemlösung anwenden. Sie bewerten die Fähigkeit des Schülers, Rückschlüsse zu ziehen und Wissen praktisch anzuwenden.",
    },
    {
      title: "Kurze Antwort",
      description:
        "Fragen, die präzise aber vollständige Antworten erfordern. Diese Fragen konzentrieren sich auf die Fähigkeit der Schüler, direkte, zielgerichtete Antworten zu geben, die ihr Verständnis effektiv demonstrieren.",
    },
  ],
  // Added French question styles
  fr: [
    {
      title: "Interprétation nouvelle génération",
      description:
        "Questions axées sur l'application des connaissances dans des contextes complexes et basés sur des scénarios. Elles mettent l'accent sur l'interprétation des données et des concepts plutôt que sur la mémorisation, et utilisent souvent des énoncés plus longs impliquant plusieurs étapes ou scénarios.",
    },
    {
      title: "Pensée analytique",
      description:
        "Questions qui demandent aux élèves de disséquer et d'examiner différents composants d'une idée, d'un événement ou d'un processus afin de comprendre la structure sous-jacente. Elles se concentrent sur l'établissement de liens entre les éléments et l'analyse des relations.",
    },
    {
      title: "Pensée critique",
      description:
        "Questions qui amènent les élèves à évaluer différents points de vue, à peser les arguments et à déterminer la validité des informations. Elles encouragent à remettre en question les hypothèses et à parvenir à des conclusions logiques.",
    },
    {
      title: "Compréhension conceptuelle",
      description:
        "Questions qui évaluent la compréhension des concepts fondamentaux par l'élève. Au lieu de se concentrer sur des détails mémorisés, ces questions demandent aux élèves d'expliquer les idées principales avec leurs propres mots.",
    },
    {
      title: "Résolution de problèmes",
      description:
        "Questions qui présentent un problème pratique et demandent aux élèves de développer une solution. Elles aident à évaluer la capacité des élèves à appliquer leurs connaissances pour surmonter des défis réels.",
    },
    {
      title: "Application au monde réel",
      description:
        "Questions qui demandent aux élèves d'appliquer des concepts théoriques à des situations de la vie réelle. Elles sont conçues pour tester les compétences pratiques et montrer comment les connaissances peuvent être utiles en dehors de la classe.",
    },
    {
      title: "Analyse comparative",
      description:
        "Questions qui demandent aux élèves de comparer et de contraster plusieurs idées, théories ou événements. Elles encouragent les élèves à évaluer de manière critique les similitudes et les différences et à exprimer des points de vue nuancés.",
    },
    {
      title: "Synthèse",
      description:
        "Questions qui demandent aux élèves de combiner plusieurs informations pour former une compréhension cohérente ou créer une nouvelle idée. Ces questions encouragent la pensée créative et l'innovation.",
    },
    {
      title: "Interprétation et application",
      description:
        "Questions axées sur l'interprétation de données, de textes ou de situations données et leur application à la résolution d'un problème. Elles évaluent la capacité de l'élève à faire des inférences et à appliquer les connaissances de manière pratique.",
    },
    {
      title: "Réponse courte",
      description:
        "Questions qui nécessitent des réponses concises mais complètes. Elles se concentrent sur la capacité des élèves à fournir des réponses directes et précises qui démontrent efficacement leur compréhension.",
    },
  ],
};

// Helper function to get grade-appropriate topics
function getGradeAppropriateTopics(
  grade: number,
  courseType: GetTopicsCourseType,
): string[] {
  const allTopics = exampleTopics[courseType] || [];

  // Basic topics for grades 5-8
  if (grade >= 5 && grade <= 8) {
    switch (courseType) {
      case "MATHEMATICS":
        return [
          "Doğal Sayılarla İşlemler",
          "Kesirler ve Ondalık Sayılar",
          "Oran ve Orantı",
          "Temel Geometrik Şekiller",
          "Çevre ve Alan Hesaplamaları",
          "Veri Analizi ve Grafikler",
          "Tam Sayılarla İşlemler",
          "Cebirsel İfadeler",
          "Denklemler",
          "Açılar ve Üçgenler",
          "Yüzdeler",
          "Örüntüler ve İlişkiler",
        ];
      case "SCIENCE":
        return [
          "Maddenin Halleri ve Özellikleri",
          "Canlılar ve Yaşam",
          "Kuvvet ve Hareket",
          "Işık ve Ses",
          "Dünya ve Uzay",
          "İnsan ve Çevre",
          "Elektrik Devreleri",
          "Vücudumuz ve Sistemleri",
          "Basit Makineler",
          "Enerji Dönüşümleri",
          "Güneş Sistemi",
          "Canlıların Sınıflandırılması",
        ];
      case "HISTORY":
        return [
          "İlk Türk Devletleri",
          "İslamiyet'in Doğuşu ve Yayılışı",
          "Türklerin İslamiyet'i Kabulü",
          "Selçuklu Devleti",
          "Osmanlı Devleti'nin Kuruluşu",
          "Coğrafi Keşifler",
          "Osmanlı Kültür ve Medeniyeti",
          "Anadolu'da İlk Yerleşimler",
          "Türk-İslam Devletleri",
          "Osmanlı Eğitim Sistemi",
          "Osmanlı'da Sosyal Hayat",
          "Anadolu Beylikleri",
        ];
      case "TURKISH":
        return [
          "Türkçe dilbilgisi",
          "İletişim ve sosyal ilişkiler",
          "Yaratıcı konuşma yapabilme",
          "Sıfatlar",
          "Zamirler",
          "Cümlenin ögeleri",
          "Türk hikaye geleneği",
          "Anlatım bozuklukları",
          "Edebi akımlar",
          "Şiir türleri",
          "Metin türleri",
          "Yazım kuralları",
          "Noktalama işaretleri",
        ];
      // Add other course types for grades 5-8...
    }
  }

  // Intermediate topics for grades 9-10
  if (grade >= 9 && grade <= 10) {
    switch (courseType) {
      case "CHEMISTRY":
        return [
          "Kimyasal Reaksiyonlar",
          "Kimyasal Formülleri",
          "Kimyasal Denklemleri",
          "Çözünme ve Çözeltiler",
          "Sürdürülebilirlik",
          "Günlük Hayatta Kimya",
          "İyonik Bağlar",
          "Kovalent Bağlar",
          "Bileşiklerin Adlandırılması",
        ];
      case "MATHEMATICS":
        return [
          "Kümeler ve Bağıntılar",
          "Fonksiyonlar",
          "Polinomlar",
          "İkinci Dereceden Denklemler",
          "Trigonometri",
          "Olasılık",
          "İstatistik",
          "Dörtgenler ve Çokgenler",
          "Benzerlik ve Dönüşümler",
          "Mantık",
          "Sayı Sistemleri",
          "Üçgende Ölçme",
        ];
      case "PHYSICS":
        return [
          "Vektörler ve Hareket",
          "Newton'un Hareket Yasaları",
          "İş, Güç ve Enerji",
          "Fizik Bilimine Giriş",
          "Kuvvet ve Hareket",
          "Enerji",
          "Basınç ve Kaldırma Kuvveti",
          "Isı ve Sıcaklık",
        ];
      case "BIOLOGY":
        return [
          "Biyoloji Bilimine Giriş",
          "Hücre Bölünmesi",
          "Hücre Zarından Madde Geçişi",
          "Canlıların Sınıflandırılması",
          "Fotosentez",
          "Kemosentez",
          "Bitki Biyolojisi",
          "Ekoloji",
          "Kalıtım",
        ];

      // Add other course types for grades 9-10...
    }
  }

  // Advanced topics for grades 11-12
  if (grade >= 11) {
    switch (courseType) {
      case "MATHEMATICS":
        return [
          "Türev ve Uygulamaları",
          "İntegral ve Uygulamaları",
          "Limit ve Süreklilik",
          "Karmaşık Sayılar",
          "Logaritma",
          "Diziler ve Seriler",
          "Analitik Geometri",
          "Vektörler",
          "Uzay Geometrisi",
          "Olasılık ve İstatistik",
          "Türev Uygulamaları",
          "İntegral Uygulamaları",
        ];
      case "PHYSICS":
        return [
          "İtme ve Momentum",
          "Elektrik Alan ve Potansiyel",
          "Manyetik Alan ve İndüksiyon",
          "Alternatif Akım",
          "Atom Fiziği",
          "Modern Fizik",
          "Kuantum Fiziği",
          "Nükleer Fizik",
          "Görelilik Teorisi",
          "Elektrostatik",
          "Elektrik Akımı",
          "Manyetizma",
          "Dalgalar",
          "Optik",
          "Modern Fizik",
          "Astronomi",
        ];
      case "BIOLOGY":
        return [
          "Dolaşım Sistemi",
          "Solunum Sistemi",
          "Boşaltım Sistemi",
          "Sinir Sistemi ve Duyu Organları",
          "Endokrin Sistem ve Hormonlar",
          "Üreme Sistemi ve Embriyonik Gelişim",
          "Genetik ve Kalıtım Mekanizmaları",
          "Evrim ve Doğal Seçilim",
        ];
      case "CHEMISTRY":
        return [
          "Kimyasal Reaksiyonlar",
          "Kimyasal Formülleri",
          "Kimyasal Denklemleri",
          "Kimyasal Tepkimelerde Hız",
          "Kimyasal Tepkimelerde Denge",
          "Entropi ve Gibbs Serbest Enerjisi",
          "Nötrallik ve pH",
          "Organik Kimya",
          "Anorganik Kimya",
          "İndirgenme Yükseltgenme Tepkimeleri",
          "Hidrokarbonlar",
          "Alkoller ve Eterler",
        ];
      // Add other course types for grades 11-12...
    }
  }

  // For AP courses, always return the original topics as they're already grade-appropriate
  if (courseType.startsWith("AP_")) {
    return allTopics;
  }

  // Return original topics if no grade-specific topics are defined
  return allTopics;
}

const PromptStep = ({
  assignmentData,
  onChange,
}: {
  assignmentData: CreateHomeworkAssignmentRequestBody;
  onChange: (data: Partial<CreateHomeworkAssignmentRequestBody>) => void;
}) => {
  const { t, i18n } = useTranslation();
  const [topic, setTopic] = useState(assignmentData.prompt || "");
  const [questionStyle, setQuestionStyle] = useState(
    assignmentData.questionStyle || "",
  );
  const [selectedGrade, setSelectedGrade] = useState(assignmentData.grade || 8);
  const [selectedCourse, setSelectedCourse] = useState(
    assignmentData.courseType ||
      (i18n.language === "tr" ? "HISTORY" : "AP_2025_WORLD_HISTORY_MODERN"),
  );
  const [selectedLanguage, setSelectedLanguage] = useState(
    assignmentData.contentLanguage || i18n.language,
  );
  const [curriculumType, setCurriculumType] = useState<CurriculumType>("MEB");
  const [internationalCurriculum, setInternationalCurriculum] =
    useState<InternationalCurriculum>("AP");
  const userEmail = useAppSelector((state) => state.user.teacher?.email);
  const shouldShowMathematics =
    userEmail === "batuhan@madlen.io" || userEmail === "kadriyezobu@gmail.com";

  useEffect(() => {
    const newCourse =
      i18n.language === "tr" ? "HISTORY" : "AP_2025_WORLD_HISTORY_MODERN";
    setSelectedCourse(newCourse);
    setTopic("");
    setQuestionStyle("");
    onChange({
      courseType: newCourse,
      prompt: "",
      questionStyle: "",
    });
  }, [i18n.language]);

  const placeholderTopic = useMemo(() => {
    return firstTopicOfCourse(
      Number(assignmentData.grade),
      assignmentData.courseType as GetTopicsCourseType,
    );
  }, [assignmentData.grade, assignmentData.courseType]);

  const getFilteredCourses = (): GetTopicsCourseType[] => {
    if (curriculumType === "MEB") {
      return Object.keys(CourseData).filter(
        (key) =>
          isTurkishCourse(key) &&
          CourseData[key as GetTopicsCourseType].visible(selectedGrade),
      ) as GetTopicsCourseType[];
    } else {
      return Object.keys(CourseData).filter(
        (key) =>
          key.startsWith(`${internationalCurriculum}_`) &&
          CourseData[key as GetTopicsCourseType].visible(selectedGrade),
      ) as GetTopicsCourseType[];
    }
  };

  const getRandomExamples = () => {
    const gradeAppropriateTopics = getGradeAppropriateTopics(
      selectedGrade,
      selectedCourse,
    );
    const randomTopic =
      gradeAppropriateTopics.length > 0
        ? gradeAppropriateTopics[
            Math.floor(Math.random() * gradeAppropriateTopics.length)
          ]
        : "";

    // Updated to support German language
    const questionStylesForLanguage =
      exampleQuestionStyles[i18n.language as "tr" | "en" | "de"] ||
      exampleQuestionStyles["en"];
    const randomStyle =
      questionStylesForLanguage.length > 0
        ? questionStylesForLanguage[
            Math.floor(Math.random() * questionStylesForLanguage.length)
          ]
        : { title: "", description: "" };

    return { topic: randomTopic, style: randomStyle };
  };

  const example1 = useMemo(
    () => getRandomExamples(),
    [selectedGrade, selectedCourse],
  );
  const example2 = useMemo(
    () => getRandomExamples(),
    [selectedGrade, selectedCourse],
  );

  const copyExample = (exampleTopic: string, exampleStyle: string) => {
    setTopic(exampleTopic);
    setQuestionStyle(exampleStyle);
    onChange({ prompt: exampleTopic, questionStyle: exampleStyle });
  };

  useEffect(() => {
    console.log("Generator changed to PROMPT");
    onChange({ generator: "PROMPT" });
  }, []);

  // Helper function to get example prefix based on language
  const getExamplePrefix = () => {
    switch (i18n.language) {
      case "tr":
        return "Örnek: ";
      case "de":
        return "Beispiel: ";
      default:
        return "Example: ";
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-blue-50 dark:bg-blue-900 p-4 rounded-lg mb-6">
        <Text variant="p" status="info" size={"sm"}>
          assignments.generator.prompt.title
        </Text>
        <Text variant="p" status="info" size={"sm"}>
          assignments.generator.prompt.description
        </Text>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <CommonPropertiesStep
          assignmentData={assignmentData}
          onChange={onChange}
        />
        <div className="space-y-2.5">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-1.5">
            <GradeSelector
              selectedGrade={selectedGrade}
              minGrade={1}
              setSelectedGrade={(grade) => {
                const availableCourses = Object.entries(CourseData).filter(
                  ([, value]) => value.visible(grade),
                );
                const firstAvailableCourse =
                  availableCourses[0]?.[0] as GetTopicsCourseType;
                const doesPreviousCourseExist = availableCourses.some(
                  ([key]) => key === selectedCourse,
                );
                setSelectedGrade(grade);
                if (!doesPreviousCourseExist) {
                  setSelectedCourse(firstAvailableCourse);
                }
                onChange({
                  grade,
                  // if previous course is in availableCourses, do not change selectedCourse else use first available course
                  ...(!doesPreviousCourseExist && {
                    courseType: firstAvailableCourse,
                  }),
                  topicIds: [],
                });
              }}
            />
          </div>
          <h4
            className={`text-sm font-medium text-gray-700 dark:text-gray-200`}
          >
            {t("hooks.form.outputLanguage")}
          </h4>
          <OutputLanguageSelector
            selectedLanguage={selectedLanguage}
            onLanguageChange={(language) => {
              setSelectedLanguage(language);
              onChange({ contentLanguage: language });
            }}
          />
        </div>
      </div>
      <div className="space-y-4">
        {/* Curriculum Type Selection */}
        <div className="space-y-2">
          <h4
            className={`text-sm font-medium text-gray-700 dark:text-gray-200`}
          >
            {t("common.curriculumType")}
          </h4>
          <div className="grid grid-cols-2 gap-3">
            <Button
              type="button"
              onClick={() => setCurriculumType("MEB")}
              variant="solid"
              size="md"
              rounded="lg"
              textColor={
                curriculumType === "MEB"
                  ? "text-white"
                  : "text-gray-600 dark:text-gray-400"
              }
              bgColor={
                curriculumType === "MEB"
                  ? `bg-indigo-600 hover:bg-indigo-700`
                  : "bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600"
              }
              fullWidth
            >
              <span className="text-sm font-medium">
                {t("common.nationalCurriculum")}
              </span>
            </Button>
            <Button
              type="button"
              onClick={() => setCurriculumType("INTERNATIONAL")}
              variant="solid"
              size="md"
              rounded="lg"
              textColor={
                curriculumType === "INTERNATIONAL"
                  ? "text-white"
                  : "text-gray-600 dark:text-gray-400"
              }
              bgColor={
                curriculumType === "INTERNATIONAL"
                  ? `bg-indigo-600 hover:bg-indigo-700`
                  : "bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600"
              }
              fullWidth
            >
              <span className="text-sm font-medium">
                {t("common.internationalCurriculum")}
              </span>
            </Button>
          </div>
        </div>

        {/* International Curriculum Sub-selection */}
        {curriculumType === "INTERNATIONAL" && (
          <div className="space-y-2">
            <h4
              className={`text-sm font-medium text-gray-700 dark:text-gray-200`}
            >
              {t("common.selectInternationalProgram")}
            </h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {(
                ["AP", "IGCSE", "IB", "ALEVEL"] as InternationalCurriculum[]
              ).map((curriculum) => (
                <Button
                  key={curriculum}
                  type="button"
                  onClick={() => setInternationalCurriculum(curriculum)}
                  variant="solid"
                  size="md"
                  rounded="lg"
                  textColor={
                    internationalCurriculum === curriculum
                      ? "text-white"
                      : "text-gray-600 dark:text-gray-400"
                  }
                  bgColor={
                    internationalCurriculum === curriculum
                      ? `bg-indigo-600 hover:bg-indigo-700`
                      : "bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600"
                  }
                  fullWidth
                >
                  <span className="text-sm font-medium">{curriculum}</span>
                </Button>
              ))}
            </div>
          </div>
        )}
      </div>
      <div className="grid grid-cols-1">
        <div className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <CourseSelector
              selectedGrade={selectedGrade}
              selectedCourse={selectedCourse}
              filteredCourses={Object.values(GetTopicsCourseType)
                .filter((course) => !getFilteredCourses().includes(course))
                .concat(
                  shouldShowMathematics
                    ? []
                    : (["MATHEMATICS"] as GetTopicsCourseType[]),
                )}
              setSelectedCourse={(course) => {
                setSelectedCourse(course);
                onChange({
                  grade: selectedGrade,
                  courseType: course,
                });
              }}
            />
          </div>
        </div>
      </div>

      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
            {t("assignments.generator.prompt.topic")}
          </label>
          <textarea
            data-testid="create-assignment-from-prompt-input"
            value={topic}
            onChange={(e) => {
              setTopic(e.target.value);
              onChange({ prompt: e.target.value, questionStyle });
            }}
            className="w-full h-20 p-4 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            placeholder={getExamplePrefix() + placeholderTopic?.topic}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
            {t("assignments.generator.prompt.questionStyle")}
          </label>
          <div className="space-y-3">
            <div className="grid grid-cols-3 sm:grid-cols-5 gap-3">
              {exampleQuestionStyles[i18n.language as "tr" | "en" | "de"]
                .map((questionStyle, index) => ({
                  label: questionStyle.title,
                  value: questionStyle.title,
                  description: questionStyle.description,
                  icon:
                    index === 0
                      ? "🎯"
                      : index === 1
                        ? "🔍"
                        : index === 2
                          ? "💡"
                          : index === 3
                            ? "⚡️"
                            : index === 4
                              ? "🎨"
                              : index === 5
                                ? "🌟"
                                : index === 6
                                  ? "🔮"
                                  : index === 7
                                    ? "📚"
                                    : index === 8
                                      ? "🧩"
                                      : "✨",
                }))
                .map((style) => (
                  <Button
                    data-testid="create-assignment-from-prompt-question-style"
                    key={style.value}
                    onClick={() => {
                      setQuestionStyle(style.value);
                      onChange({ prompt: topic, questionStyle: style.value });
                    }}
                    rounded="lg"
                    bgColor={
                      questionStyle === style.value
                        ? "bg-indigo-50 dark:bg-indigo-900/30"
                        : "hover:bg-indigo-50 dark:hover:bg-indigo-900/20"
                    }
                    textColor={
                      questionStyle === style.value
                        ? "text-indigo-700 dark:text-indigo-300"
                        : ""
                    }
                    size="md"
                    className={`relative flex items-center justify-center border-2 ${
                      questionStyle === style.value
                        ? "border-indigo-500"
                        : "border-gray-200 dark:border-gray-700 hover:border-indigo-300 dark:hover:border-indigo-600"
                    }`}
                  >
                    <div className="text-center">
                      <span className="text-2xl mb-2 block">{style.icon}</span>
                      <span className="text-sm font-medium">{style.label}</span>
                      <div className="absolute top-1 right-1 group">
                        <svg
                          className="w-4 h-4 text-gray-400"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                        <div className="absolute bottom-full right-0 mb-2 w-48 p-2 bg-gray-800 text-white text-xs rounded-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all">
                          {style.description}
                        </div>
                      </div>
                    </div>
                  </Button>
                ))}
            </div>
          </div>
        </div>

        {/* <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
          <Text
            variant="h4"
            className="text-sm font-medium text-gray-700 dark:text-gray-200 mb-3"
          >
            assignments.generator.prompt.examples
          </Text>
          <div className="grid grid-cols-1 gap-3">
            <div
              className="group relative p-3 bg-white dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600 cursor-pointer hover:border-indigo-300 dark:hover:border-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-900/50 transition-all shadow-sm hover:shadow-md"
              onClick={() => copyExample(example1.topic, example1.style.title)}
            >
              <div className="absolute inset-0 bg-gradient-to-r from-indigo-500/0 to-indigo-500/0 group-hover:from-indigo-500/5 group-hover:to-indigo-500/10 transition-all rounded-lg" />
              <Text
                variant="p"
                className="text-sm font-medium text-gray-700 dark:text-gray-200 mb-1"
              >
                assignments.generator.prompt.topic
              </Text>
              <Text
                variant="p"
                className="text-sm text-gray-600 dark:text-gray-300"
              >
                {example1.topic}
              </Text>
              <Text
                variant="p"
                className="text-sm font-medium text-gray-700 dark:text-gray-200 mt-2 mb-1"
              >
                assignments.generator.prompt.questionStyle
              </Text>
              <Text
                variant="p"
                className="text-sm text-gray-600 dark:text-gray-300"
              >
                {example1.style.title}
              </Text>
            </div>
            <div
              className="group relative p-3 bg-white dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600 cursor-pointer hover:border-indigo-300 dark:hover:border-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-900/50 transition-all shadow-sm hover:shadow-md"
              onClick={() => copyExample(example2.topic, example2.style.title)}
            >
              <div className="absolute inset-0 bg-gradient-to-r from-indigo-500/0 to-indigo-500/0 group-hover:from-indigo-500/5 group-hover:to-indigo-500/10 transition-all rounded-lg" />
              <Text
                variant="p"
                className="text-sm font-medium text-gray-700 dark:text-gray-200 mb-1"
              >
                assignments.generator.prompt.topic
              </Text>
              <Text
                variant="p"
                className="text-sm text-gray-600 dark:text-gray-300"
              >
                {example2.topic}
              </Text>
              <Text
                variant="p"
                className="text-sm font-medium text-gray-700 dark:text-gray-200 mt-2 mb-1"
              >
                assignments.generator.prompt.questionStyle
              </Text>
              <Text
                variant="p"
                className="text-sm text-gray-600 dark:text-gray-300"
              >
                {example2.style.title}
              </Text>
            </div>
          </div>
        </div> */}
      </div>
    </div>
  );
};

export default PromptStep;
